import { SmartLink } from "@selector/shared/components";
import { clsx } from "clsx";
import { type FC, type ReactNode, useRef } from "react";
import { DialogComponent, Label, Popover, TriggerButton, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";

import style from "./styles/HeaderWidgets.module.scss";

export interface AttributeWidgetProps {
  label: string;
  description?: ReactNode;
  linkTo?: string;
  forceWidth?: number;
}

/**
 * Generates a horizontal list where each element is a label-value pair.
 * Each label-value pair is stacked vertically:
 *
 *   Labels:    GENE NAME    GENE TYPE    ...
 *   Values:    FumA         mRNA         ...
 */
const AttributeWidget: FC<AttributeWidgetProps> = (props) => {
  if (!props.description) return null;

  let descElement = props.description;
  if (props.linkTo) {
    descElement = <SmartLink to={props.linkTo}>{descElement}</SmartLink>;
  }

  return (
    <VStack spacing={2} className={style.attributes_wrapper} style={{ width: props.forceWidth, maxWidth: "unset" }}>
      <Label>{props.label}</Label>
      <div className={style.attributes_subtitle}>{descElement}</div>
    </VStack>
  );
};

// --- Responsive List ---
interface AttributesListWidgetProps {
  attributes: AttributeWidgetProps[];
  wrap?: boolean;
  isUiSmall?: boolean; // from PageHeader
}

/** @deprecated Use HeaderBar instead of PageHeader */
const AttributesListWidget: FC<AttributesListWidgetProps> = (props) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay({});

  return props.isUiSmall ? (
    <>
      <TriggerButton {...triggerProps} icon={"info"} label={"More"} showLabel ref={triggerRef} />
      <Popover state={state} {...overlayProps} triggerRef={triggerRef}>
        <DialogComponent>
          <div className={clsx(style.attributes_list)}>
            {props.attributes
              ?.filter((a) => a)
              .map((attribute: AttributeWidgetProps, i) => (
                <AttributeWidget {...attribute} key={`header-attribute-${i}`} />
              ))}
          </div>
        </DialogComponent>
      </Popover>
    </>
  ) : (
    <div className={clsx(style.attributes_list, { [style.attributes_list_wrap]: props.wrap === true })}>
      {props.attributes?.map((attribute, i) => <AttributeWidget {...attribute} key={`header-attribute-${i}`} />)}
    </div>
  );
};

export { AttributesListWidget };
